import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, Smile, Battery, Brain, Moon, Scale, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRICS = [
  { key: 'mood', label: 'Mood', icon: Smile, stroke: '#f43f5e', inverse: false },
  { key: 'energy', label: 'Energy', icon: Battery, stroke: '#f59e0b', inverse: false },
  { key: 'stress', label: 'Stress', icon: Brain, stroke: '#ef4444', inverse: true },
  { key: 'sleep', label: 'Sleep Quality', icon: Moon, stroke: '#6366f1', inverse: false },
  { key: 'work_life', label: 'Work-Life Balance', icon: Scale, stroke: '#10b981', inverse: false },
];

const RANGE_PRESETS = [
  { key: 7, label: '7d' },
  { key: 14, label: '14d' },
  { key: 30, label: '30d' },
  { key: 90, label: '90d' },
];

function formatDateShort(iso) {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function avg(values) {
  if (!values.length) return null;
  return Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10;
}

function trend(values) {
  // Compare first half vs second half average
  if (values.length < 4) return 'flat';
  const mid = Math.floor(values.length / 2);
  const first = avg(values.slice(0, mid));
  const second = avg(values.slice(mid));
  if (Math.abs(second - first) < 0.5) return 'flat';
  return second > first ? 'up' : 'down';
}

export default function WelfareTrends() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);

  useEffect(() => {
    loadHistory(range);
  }, [range]);

  const loadHistory = async (days) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/welfare/history`, { params: { days } });
      setEntries(res.data || []);
    } catch (error) {
      console.error('Failed to load welfare history:', error);
    } finally {
      setLoading(false);
    }
  };

  const chartData = useMemo(() => {
    return entries.map(e => ({
      date: e.date,
      label: formatDateShort(e.date),
      mood: e.mood,
      energy: e.energy,
      stress: e.stress,
      sleep: e.sleep,
      work_life: e.work_life,
    }));
  }, [entries]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
      </div>
    );
  }

  if (entries.length < 3) {
    return (
      <Card data-testid="welfare-trends-empty">
        <CardContent className="py-16 text-center">
          <TrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-300" strokeWidth={1.5} />
          <h3 className="text-lg font-semibold text-gray-900">Not enough data yet</h3>
          <p className="text-gray-500 mt-1">Check in on at least 3 days to see your trends.</p>
          <p className="text-xs text-gray-400 mt-3">You currently have {entries.length} {entries.length === 1 ? 'check-in' : 'check-ins'}.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="welfare-trends-container">
      {/* Range selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-gray-500">{entries.length} check-ins over the last {range} days</p>
        <div className="flex gap-2" data-testid="welfare-trends-range">
          {RANGE_PRESETS.map(r => (
            <Button
              key={r.key}
              size="sm"
              variant={range === r.key ? 'default' : 'outline'}
              onClick={() => setRange(r.key)}
              className={range === r.key ? 'bg-rose-500 hover:bg-rose-600 text-white' : ''}
              data-testid={`welfare-trends-range-${r.key}`}
            >
              {r.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {METRICS.map(m => {
          const Icon = m.icon;
          const values = chartData.map(d => d[m.key]);
          const average = avg(values);
          const direction = trend(values);
          const isPositive = m.inverse ? direction === 'down' : direction === 'up';
          const isNegative = m.inverse ? direction === 'up' : direction === 'down';
          const TrendIcon = direction === 'flat' ? Minus : (direction === 'up' ? TrendingUp : TrendingDown);
          const trendColor = direction === 'flat' ? 'text-gray-400' : (isPositive ? 'text-emerald-600' : (isNegative ? 'text-red-500' : 'text-gray-400'));
          return (
            <Card key={m.key} data-testid={`welfare-summary-${m.key}`}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-1">
                  <Icon className="w-4 h-4" style={{ color: m.stroke }} strokeWidth={1.5} />
                  <TrendIcon className={`w-4 h-4 ${trendColor}`} strokeWidth={2} />
                </div>
                <p className="text-xs text-gray-500">{m.label}</p>
                <p className="text-2xl font-bold tabular-nums" style={{ color: m.stroke }}>{average ?? '—'}</p>
                <p className="text-xs text-gray-400">avg / 10</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {METRICS.map(m => {
          const Icon = m.icon;
          return (
            <Card key={m.key} data-testid={`welfare-chart-${m.key}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Icon className="w-4 h-4" style={{ color: m.stroke }} strokeWidth={1.5} />
                  {m.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis domain={[1, 10]} ticks={[1, 4, 7, 10]} tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ fontWeight: 600 }}
                    />
                    <Line
                      type="monotone"
                      dataKey={m.key}
                      stroke={m.stroke}
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: m.stroke }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
